import type { GameSession as CoreGameSession, SessionSnapshot } from '../core/session';
import type { RouteId } from '../core/types';

type GameId = Extract<RouteId, 'game1' | 'game2' | 'game3'>;
type AttemptStatus = 'completed' | 'abandoned';

interface CreatedGameSession {
  readonly id: string;
}

interface ActiveRun {
  readonly gameId: GameId;
  readonly clientSessionId: string;
  readonly startedAt: string;
  readonly startedAtMs: number;
  session: Promise<CreatedGameSession>;
  finished: boolean;
}

interface Game3ResultDetail {
  readonly game: 'game3';
  readonly completed: true;
  readonly score: number;
  readonly durationSec: number;
}

function gameRoute(route: RouteId): route is GameId {
  return route === 'game1' || route === 'game2' || route === 'game3';
}

function game3Result(value: unknown): value is Game3ResultDetail {
  if (!value || typeof value !== 'object') return false;
  const detail = value as Partial<Game3ResultDetail>;
  return detail.game === 'game3' && detail.completed === true && Number.isFinite(detail.score)
    && Number.isFinite(detail.durationSec) && Number(detail.durationSec) >= 0;
}

function clientSessionId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `run-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function cmsApiBase(): string {
  const env = (import.meta as ImportMeta & { readonly env?: ImportMetaEnv }).env;
  return (env?.VITE_CMS_API_BASE_URL ?? '').trim().replace(/\/$/, '');
}

/**
 * Connects the three live game scenes to the CMS attempt API.
 *
 * The reporter intentionally fails open: temporary reporting trouble must not
 * interrupt a student's game. Server-side tenant and role checks still decide
 * whether a session may be created or completed.
 */
export class GameProgressReporter {
  private active?: ActiveRun;
  private readonly unsubscribe: () => void;
  private readonly apiBase = cmsApiBase();

  constructor(private readonly coreSession: CoreGameSession) {
    this.unsubscribe = coreSession.subscribe((snapshot) => this.observe(snapshot));
    window.addEventListener('cca:game3-result', this.onGame3Result);
  }

  entered(route: RouteId): void {
    if (!gameRoute(route)) return;
    if (this.active && !this.active.finished) this.finish(this.coreSession.score, 'abandoned');
    const run: ActiveRun = {
      gameId: route,
      clientSessionId: clientSessionId(),
      startedAt: new Date().toISOString(),
      startedAtMs: performance.now(),
      session: Promise.resolve({ id: '' }),
      finished: false,
    };
    run.session = this.createSession(run).catch((reason: unknown) => {
      console.warn('[cms] game session was not recorded', reason);
      return { id: '' };
    });
    this.active = run;
    this.observe(this.coreSession.snapshot());
  }

  exiting(route: RouteId): void {
    if (!this.active || this.active.gameId !== route || this.active.finished) return;
    this.finish(this.coreSession.score, 'abandoned');
  }

  dispose(): void {
    if (this.active && !this.active.finished) this.finish(this.coreSession.score, 'abandoned');
    this.unsubscribe();
    window.removeEventListener('cca:game3-result', this.onGame3Result);
  }

  private observe(snapshot: SessionSnapshot): void {
    const gameId = this.active?.gameId;
    if (!gameId || this.active?.finished) return;
    if (gameId === 'game1' && (snapshot.phase === 'won' || snapshot.phase === 'lost')) {
      this.finish(snapshot.score, 'completed');
    } else if (gameId === 'game2' && snapshot.total > 0 && snapshot.collected >= snapshot.total) {
      this.finish(snapshot.score, 'completed');
    }
  }

  private readonly onGame3Result = (event: Event): void => {
    if (this.active?.gameId !== 'game3' || this.active.finished) return;
    const detail = event instanceof CustomEvent ? event.detail : undefined;
    if (!game3Result(detail)) return;
    this.finish(detail.score, 'completed', Math.round(detail.durationSec * 1000));
  };

  private finish(score: number, status: AttemptStatus, explicitDurationMs?: number): void {
    const run = this.active;
    if (!run || run.finished) return;
    run.finished = true;
    const measured = Math.round(Math.max(0, performance.now() - run.startedAtMs));
    const durationMs = Math.min(86_400_000, Math.max(0, explicitDurationMs ?? measured));
    void run.session
      .then((session) => {
        if (!session.id) return;
        return this.request(`/api/game-sessions/${encodeURIComponent(session.id)}/complete`, {
          method: 'PUT',
          body: JSON.stringify({
            score: Number.isFinite(score) ? score : 0,
            durationMs,
            status,
            completedAt: new Date().toISOString(),
          }),
          keepalive: true,
        });
      })
      .catch((reason: unknown) => console.warn('[cms] game completion was not recorded', reason));
  }

  private async createSession(run: ActiveRun): Promise<CreatedGameSession> {
    const response = await this.request('/api/game-sessions', {
      method: 'POST',
      body: JSON.stringify({
        gameId: run.gameId,
        clientSessionId: run.clientSessionId,
        startedAt: run.startedAt,
      }),
    });
    const payload = (await response.json()) as Partial<CreatedGameSession>;
    if (!payload.id) throw new Error('The CMS returned an invalid game session.');
    return { id: payload.id };
  }

  private request(path: string, init: RequestInit): Promise<Response> {
    const base = this.apiBase ? `${this.apiBase}/` : window.location.origin;
    const url = new URL(path.replace(/^\//, ''), base);
    const headers = new Headers(init.headers);
    headers.set('accept', 'application/json');
    if (init.body) headers.set('content-type', 'application/json');
    return fetch(url, { ...init, credentials: 'include', headers }).then((response) => {
      if (!response.ok) throw new Error(`CMS game reporting failed (${response.status}).`);
      return response;
    });
  }
}
